import React, { useState, useEffect  } from 'react';
import * as constants from "../../constants/appConstants";
import { getRepositories, getAllDevelopersFromRepo } from '../../services/apiServices';
import { Button, Col, FloatingLabel, Form, Row } from 'react-bootstrap';
import DeveloperMultiSelect from '../common/DeveloperMultiSelect';
import PropTypes from 'prop-types';
import "react-datepicker/dist/react-datepicker.css"; // Import CSS
import CustomDatePicker from './CustomDatePicker';
import RepositoryMultiSelect from '../common/RepositoryMultiSelect';

const SearchForm = ({ setShowModal, setFormData, startDate, setStartDate, endDate, setEndDate }) => {
    const [platform, setPlatform] = useState('AzureDevOps');
    const [repositories, setRepositories] = useState(constants.defaultDropDownValues.repositories);
    const [allDevelopers, setAllDevelopers] = useState(constants.defaultDropDownValues.developers);
    const [selectedRepositoryId, setSelectedRepositoryId] = useState([]);
    const [selectedDeveloperNames, setSelectedDeveloperNames] = useState([]);
    const [getGitMetrics, setGetGitMetrics] = useState(false);

    // Determine if all developers are selected
    const isAllSelected = allDevelopers.length > 0 && allDevelopers.every(dev => dev.selected);

    const handleSearchClick = async (e) => {
        e.preventDefault();

        if (selectedRepositoryId.length === 0) {
          alert("Please select at least one repository before searching.");
          return;
        }
      
        if (selectedDeveloperNames.length === 0) {
          alert("Please select at least one developer before searching.");
          return;
        }
      
        if (!getGitMetrics) {
              const formattedStartDate = startDate instanceof Date
              ? startDate.toLocaleDateString('sv-SE')
              : startDate;
        
              const formattedEndDate = endDate instanceof Date
                ? endDate.toLocaleDateString('sv-SE')
                : endDate;

            const selectedRepoNames = repositories
            .filter(repo => selectedRepositoryId.includes(String(repo.ID)))
            .map(repo => repo.name);  
        
            const body = {
                platform,
                selectedRepositoryId: selectedRepositoryId.map(String), // always array of strings
                selectedRepositoryNames: selectedRepoNames,
                selectedDeveloperNames: selectedDeveloperNames,
                startDate: formattedStartDate,
                endDate: formattedEndDate,
            };
        
            setShowModal(true);
            setGetGitMetrics(true);
            setFormData(body); // this gets picked up in HomePage
            }
        };

    const handlePlatformChange = async (event) => {
        setGetGitMetrics(false);
        setFormData(null);
        setSelectedDeveloperNames([]);
    
        const selectedPlatform = event.target.value; //  This is just a string
        if (selectedPlatform !== "SelectPlatform") {
            setPlatform(selectedPlatform); // Set the platform string
            setSelectedRepositoryId([]);   // Clear previous repo selections
            setSelectedDeveloperNames([]); 
            setAllDevelopers(constants.defaultDropDownValues.developers);
    
            const allRepositories = await getRepositories(selectedPlatform);
            allRepositories.sort((a, b) =>
                a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase())
            );
            setRepositories(allRepositories);
        } else {
            setRepositories(constants.defaultDropDownValues.repositories);
            setAllDevelopers(constants.defaultDropDownValues.developers);
        }
    };

    const allReposSelected = repositories.length > 0 &&
    repositories.every(repo => selectedRepositoryId.includes(String(repo.ID)));
  
    const handleRepoMultiSelection = async (event) => {
        const { value, checked } = event.target;
        const repoId = String(value);
      
        setGetGitMetrics(false);
        setFormData(null);
      
        let updatedRepoIds;
        if (checked) {
          updatedRepoIds = [...selectedRepositoryId, repoId];
        } else {
          updatedRepoIds = selectedRepositoryId.filter(id => id !== repoId);
        }
      
        setSelectedRepositoryId(updatedRepoIds);
      
        // Fetch developers for all selected repos
        const allDevMap = new Map();
        const loggedInUserDetails = JSON.parse(localStorage.getItem('loggedInUserDetails'));
        const devsFromAllRepos = await getAllDevelopersFromRepo(updatedRepoIds, loggedInUserDetails);
        devsFromAllRepos.forEach(dev => {
          allDevMap.set(dev.value, { ...dev, selected: false });
        });
      
        const combinedDevs = Array.from(allDevMap.values()).sort((a, b) =>
          a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase())
        );
      
        setAllDevelopers(combinedDevs);
        setSelectedDeveloperNames([]); // Clear selected devs on repo change
      };
      
    
      const handleRepoSelectAll = async (event) => {
        const { checked } = event.target;
        setGetGitMetrics(false);
        setFormData(null);
      
        let updatedRepoIds = [];
        if (checked) {
          updatedRepoIds = repositories.map(repo => String(repo.ID));
        }
      
        setSelectedRepositoryId(updatedRepoIds);
      
        // Fetch developers for all selected repos (only if any selected)
        if (updatedRepoIds.length > 0) {
          const allDevMap = new Map();
          const loggedInUserDetails = JSON.parse(localStorage.getItem('loggedInUserDetails'));
          const devsFromAllRepos = await getAllDevelopersFromRepo(updatedRepoIds, loggedInUserDetails);
      
          devsFromAllRepos.forEach(dev => {
            allDevMap.set(dev.value, { ...dev, selected: false });
          });
      
          const combinedDevs = Array.from(allDevMap.values()).sort((a, b) =>
            a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase())
          );
      
          setAllDevelopers(combinedDevs);
          setSelectedDeveloperNames([]); // Clear previously selected devs
        } else {
          setAllDevelopers(constants.defaultDropDownValues.developers);
          setSelectedDeveloperNames([]);
        }
      };

    const handleDevSelection = (event) => {
        setGetGitMetrics(false);
        setFormData(null);
        const { value, checked } = event.target;
        setSelectedDeveloperNames((prevSelected) => {
            if (checked) {
                return [...prevSelected, value]; // Add new selection
            } else {
                return prevSelected.filter((developer) => developer !== value); // Remove selection
            }
        });
        setAllDevelopers((prevDevelopers) => {
            const updatedDevelopers = prevDevelopers.map((dev) =>
                dev.value === value ? { ...dev, selected: checked } : dev
            );
            return updatedDevelopers;
        });
    };

    // Handle "Select All" checkbox change
    const handleSelectAll = (event) => {
        const { checked } = event.target;
        setGetGitMetrics(false);
        setFormData(null);
        setAllDevelopers((prevDevelopers) =>
            prevDevelopers.map((dev) => ({ ...dev, selected: checked }))
        );
        setSelectedDeveloperNames(checked ? allDevelopers.map(dev => dev.value) : []);
    };

    const handleProjectToggle = async (projectName, reposInProject) => {
        setGetGitMetrics(false);
        setFormData(null);
      
        const repoIds = reposInProject.map(repo => String(repo.ID));
        const allSelected = repoIds.every(id => selectedRepositoryId.includes(id));
      
        let updatedRepoIds;
        if (allSelected) {
          // Deselect all repos from this project
          updatedRepoIds = selectedRepositoryId.filter(id => !repoIds.includes(id));
        } else {
          // Select all repos from this project
          const newSet = new Set([...selectedRepositoryId, ...repoIds]);
          updatedRepoIds = Array.from(newSet);
        }
      
        setSelectedRepositoryId(updatedRepoIds);
      
        //  Now fetch developers from all updated selected repos
        const allDevMap = new Map();
        // for (const id of updatedRepoIds) {
          const loggedInUserDetails = JSON.parse(localStorage.getItem('loggedInUserDetails'));
          const devs = await getAllDevelopersFromRepo(updatedRepoIds, loggedInUserDetails);
          devs.forEach(dev => {
            allDevMap.set(dev.value, { ...dev, selected: false });
          });
        // }
      
        const combinedDevs = Array.from(allDevMap.values()).sort((a, b) =>
          a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase())
        );
      
        setAllDevelopers(combinedDevs);
        setSelectedDeveloperNames([]);
      };

    useEffect(() => {
        const loadDefaultRepositories = async () => {
          const defaultPlatform = 'AzureDevOps';
      
          try {
            const allRepositories = await getRepositories(defaultPlatform);
            allRepositories.sort((a, b) =>
              a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase())
            );
            setRepositories(allRepositories);
      
            //  Find the xspaces repo
            const xspacesRepo = allRepositories.find(repo =>
              repo.name.trim().toLowerCase() === 'xspaces'
            );
      
            if (xspacesRepo) {
                setSelectedRepositoryId([String(xspacesRepo.ID)]);
              //  Also fetch developers for this repo
              const loggedInUserDetails = JSON.parse(localStorage.getItem('loggedInUserDetails'));
              const devList = await getAllDevelopersFromRepo(xspacesRepo.ID, loggedInUserDetails);
              devList.sort((a, b) => a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase()));
              devList.forEach(dev => {
                dev.selected = true; // Preselect all developers
              });
              setAllDevelopers(devList);
      
              // Set developer names to "Select All"
              const devNames = devList.map(dev => dev.value);
              setSelectedDeveloperNames(devNames);
      
              // trigger metrics load by setting formData here
              setFormData({
                platform: defaultPlatform,
                selectedRepositoryId: [xspacesRepo.ID],
                selectedDeveloperNames: devNames,
                startDate,
                endDate,
              });
            }
          } catch (error) {
            console.error('Error loading default repositories or developers:', error);
          }
        };
      
        loadDefaultRepositories();
      }, []);

    return (
        <Form className='search-form-wrapper'>
            <Row>
                <Col>
                    <FloatingLabel
                        label="Platform"
                        className="mb-3"
                    >
                        <Form.Select
                            id="platform"
                            value={platform}
                            onChange={(e) => handlePlatformChange(e)}
                        >
                            {constants.platforms.map(eachPlatform => (
                                <option value={eachPlatform.key} key={eachPlatform.key}>
                                    {eachPlatform.name}
                                </option>
                            ))}
                        </Form.Select>
                    </FloatingLabel>
                </Col>
                <Col>
                <RepositoryMultiSelect
                    repositories={repositories}
                    selectedRepositoryId={selectedRepositoryId}
                    handleRepoSelection={handleRepoMultiSelection}
                    handleSelectAll={handleRepoSelectAll}
                    handleProjectToggle={handleProjectToggle}
                    isAllSelected={allReposSelected}
                    />
                </Col>
                <Col>
                    <DeveloperMultiSelect
                        allDevelopers={allDevelopers}
                        handleDevSelection={handleDevSelection}
                        handleSelectAll={handleSelectAll}
                        isAllSelected={isAllSelected}
                        selectedDeveloperNames = {selectedDeveloperNames}
                    />
                </Col>
                <Col className='mb-3 me-md-4'>
                    <CustomDatePicker
                        label='Start Date'
                        id='startDate'
                        date={startDate}
                        onChange={(date) => {
                            setGetGitMetrics(false);
                            setStartDate(date)
                        }}
                    />
                </Col>
                <Col className='mb-3 me-md-4'>
                    <CustomDatePicker
                        label='End Date'
                        id='endDate'
                        date={endDate}
                        onChange={(date) => {
                            setGetGitMetrics(false);
                            const endOfDay = new Date(date)
                            endOfDay.setHours(23,59,59,999)
                            setEndDate(endOfDay)
                        }}
                    />
                </Col>
                <Col className='mb-3 me-md-4'>
                    <Button onClick={handleSearchClick} className="search-button" variant="outline-primary">
                        <i className="fa fa-search" aria-hidden="true"></i> &nbsp; &nbsp;Search
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

SearchForm.propTypes = {
    setShowModal: PropTypes.func.isRequired,
    setFormData: PropTypes.func.isRequired,
    startDate: PropTypes.any.isRequired,
    setStartDate: PropTypes.func.isRequired,
    endDate: PropTypes.any.isRequired,
    setEndDate: PropTypes.func.isRequired,
}

export default SearchForm;